import { ApiClientAxiosInstance } from 'common/api';
import { SalonizedSyncResult } from 'common/api/generated-api';

// Single, organisation-agnostic OAuth redirect path. Only this URI needs to be registered in
// Google Cloud (per environment). The organisation is carried in the OAuth `state` parameter.
export const GOOGLE_OAUTH_CALLBACK_PATH = '/oauth/google-calendar/callback';

export const getGoogleOAuthRedirectUri = () => `${window.location.origin}${GOOGLE_OAUTH_CALLBACK_PATH}`;

export interface GoogleEmployeeCalendar {
  employeeId: string;
  calendarId: string;
  calendarName?: string;
}

export interface GoogleCalendarConnection {
  id: string;
  salonId: string;
  connected: boolean;
  needsReauth?: boolean;
  calendarEmail?: string;
  calendarId?: string;
  calendarName?: string;
  calendarMode?: 'salon' | 'employee';
  employeeCalendars?: GoogleEmployeeCalendar[];
  connectedAt?: string;
}

export interface GoogleCalendarConfigRequest {
  mode: 'salon' | 'employee';
  calendarId?: string;
  calendarName?: string;
  employeeCalendars?: GoogleEmployeeCalendar[];
}

export interface GoogleCalendarListItem {
  id: string;
  name: string;
  primary: boolean;
}

export interface GoogleCalendarEvent {
  id: string;
  summary: string;
  description?: string;
  startTime: string;
  endTime: string;
  attendees: string[];
}

export interface GoogleCalendarEventsResponse {
  connected: boolean;
  events: GoogleCalendarEvent[];
  calendarEmail?: string;
}

const basePath = (organisationId: string, salonId: string) =>
  `/organisations/${organisationId}/salons/${salonId}/google-calendar`;

const GoogleCalendarClient = {
  getStatus: (organisationId: string, salonId: string) =>
    ApiClientAxiosInstance.get<GoogleCalendarConnection>(`${basePath(organisationId, salonId)}/status`),

  connect: (organisationId: string, salonId: string, code: string, redirectUri: string) =>
    ApiClientAxiosInstance.post<GoogleCalendarConnection>(`${basePath(organisationId, salonId)}/connect`, {
      code,
      redirectUri,
    }),

  disconnect: (organisationId: string, salonId: string) =>
    ApiClientAxiosInstance.delete(`${basePath(organisationId, salonId)}/disconnect`),

  getEvents: (organisationId: string, salonId: string, startDate?: string, endDate?: string) =>
    ApiClientAxiosInstance.get<GoogleCalendarEventsResponse>(`${basePath(organisationId, salonId)}/events`, {
      params: { startDate, endDate },
    }),

  getOAuthUrl: (organisationId: string, salonId: string, redirectUri: string, state?: string) =>
    ApiClientAxiosInstance.get<{ url: string }>(`${basePath(organisationId, salonId)}/oauth-url`, {
      params: { redirectUri, state },
    }),

  getCalendars: (organisationId: string, salonId: string) =>
    ApiClientAxiosInstance.get<GoogleCalendarListItem[]>(`${basePath(organisationId, salonId)}/calendars`),

  selectCalendar: (organisationId: string, salonId: string, calendarId: string, calendarName: string) =>
    ApiClientAxiosInstance.put<GoogleCalendarConnection>(`${basePath(organisationId, salonId)}/calendar`, {
      calendarId,
      calendarName,
    }),

  // Set the coupling mode + selection (salon-wide single calendar, or a calendar per treating employee).
  setCalendarConfig: (organisationId: string, salonId: string, body: GoogleCalendarConfigRequest) =>
    ApiClientAxiosInstance.put<GoogleCalendarConnection>(`${basePath(organisationId, salonId)}/calendar-config`, body),

  // Pull the Google agenda into the shared appointments table (−2 weeks / +365 days, with prune).
  sync: (organisationId: string, salonId: string) =>
    ApiClientAxiosInstance.post<SalonizedSyncResult>(`${basePath(organisationId, salonId)}/sync`),

  // Backfill the last 12 months (add/update only, never prunes).
  syncPastYear: (organisationId: string, salonId: string) =>
    ApiClientAxiosInstance.post<SalonizedSyncResult>(`${basePath(organisationId, salonId)}/sync-past-year`),
};

export default GoogleCalendarClient;
